import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import * as builderTemplatesActions from '../actions/builder-templates.action';
import {
  LoadBuilderTemplatesCategories,
  LoadImportingBuilderTemplate,
  LoadLastBuilderTemplate
} from '../actions/builder-templates.action';
import {DbResponse} from "../../shared/interceptors/db-response";
import {filter, map, switchMap} from 'rxjs/operators';
import {Utils} from "../../shared/utils/utils";
import {BuilderTemplatesService} from "../../websites/website/builder-templates/builder-templates.service";
import * as ongoingActivityActions from '../actions';
import {AddActivity, UpdateOngoingActivityState} from '../actions';
import {ActivityAction, ActivityStatus, ActivityType, IActivity} from "../../activities/activity";
import * as activityActions from "../actions/activities.action";
import {DbStoreState} from "../reducers";
import {TypeUtils} from "../../shared/utils/type-utils";
import {Store} from "@ngrx/store";

@Injectable()
export class BuilderTemplatesEffects {
  loadBuilderTemplates$ = createEffect(() => this._actions$.pipe(
    ofType(builderTemplatesActions.LOAD_BUILDER_TEMPLATES),
    switchMap(() => {
      return this._builderTemplatesService.getBuilderTemplates()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new builderTemplatesActions.LoadBuilderTemplatesSuccess(Utils.arrayToEntities(response.data, 'id'));
            }
            return new builderTemplatesActions.LoadBuilderTemplatesFail(response.message);
          })
        );
    })
  ));
  loadLastBuilderTemplate$ = createEffect(() => this._actions$.pipe(
    ofType(builderTemplatesActions.LOAD_LAST_BUILDER_TEMPLATE),
    switchMap((action: LoadLastBuilderTemplate) => {
      return this._builderTemplatesService.getLastBuilderTemplate(action.payload.websiteId)
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new builderTemplatesActions.LoadLastBuilderTemplateSuccess({
                websiteId: action.payload.websiteId,
                lastEntities: response.data
              });
            }
            return new builderTemplatesActions.LoadLastBuilderTemplateFail(response.message);
          })
        );
    })
  ));
  loadBuilderTemplatesCategories$ = createEffect(() => this._actions$.pipe(
    ofType(builderTemplatesActions.LOAD_BUILDER_TEMPLATES_CATEGORIES),
    switchMap((action: LoadBuilderTemplatesCategories) => {
      return this._builderTemplatesService.getBuilderTemplatesCategories()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new builderTemplatesActions.LoadBuilderTemplatesCategoriesSuccess(response.data);
            }
            return new builderTemplatesActions.LoadBuilderTemplatesCategoriesFail(response.message);
          })
        );
    })
  ));

  /*@Effect() updateWebsiteSubData$ = this._actions$.ofType(UPDATE_WEBSITE_SUB_DATA)
    .pipe(
      switchMap((action: UpdateWebsiteSubData) => {
        return [
          new builderTemplatesActions.LoadBuilderTemplates(),
          new builderTemplatesActions.LoadBuilderTemplatesCategories,
          new builderTemplatesActions.LoadLastBuilderTemplate(action.payload.id)
        ];
      })
    );*/
  private readonly _pendingActivityMap: { [activityId: string]: IActivity };
  loadImportingBuilderTemplate$ = createEffect(() => this._actions$.pipe(
    ofType(builderTemplatesActions.LOAD_IMPORTING_BUILDER_TEMPLATE),
    switchMap((action: LoadImportingBuilderTemplate) => {
      const payload = action.payload;
      const websiteId: number = payload.website.id;
      const activityId: string = websiteId + 'ImportTemplate';
      const activity: IActivity = this.generateImportTemplatePendingActivity(activityId, websiteId);

      this._pendingActivityMap[activityId] = activity;

      return this._builderTemplatesService.importBuilderTemplates(websiteId, payload.templateId, payload.templateType, payload.templateTitle, payload.postStatus, payload.kitData, payload.menuItemPosition, payload.menuItemId, payload.menuTermId)
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new builderTemplatesActions.LoadImportingBuilderTemplateSuccess(response.data.template);
            } else {
              delete this._pendingActivityMap[activityId];
            }
            return new builderTemplatesActions.LoadImportingBuilderTemplateFail(response.message);
          })
        );
    })
  ));
  startBuilderImportActivitySimulation$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityAction.IMPORT_HOSTED_TEMPLATE, ActivityAction.IMPORT_TEMPLATE
    ]).includes(action.payload.action)),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityStatus.RUNNING
    ]).includes(action.payload.status)),
    filter((action: AddActivity): boolean => {
      const activity: IActivity = action.payload;
      const activityId: string = activity.websiteId.toString() + 'ImportTemplate';
      if (TypeUtils.isEmpty(this._pendingActivityMap)) {
        this._pendingActivityMap[activityId] = activity;
      }
      return !!this._pendingActivityMap[activityId];
    }),
    map((action: AddActivity) => {
      const activity: IActivity = action.payload;
      const activityId: string = activity.websiteId.toString() + 'ImportTemplate';

      delete this._pendingActivityMap[activityId];

      if (activity.progress === 0) {
        this.simulateActivityProgress(activity);
      }

      return new ongoingActivityActions.RemoveOngoingActivity({
        websiteId: activity.websiteId,
        activityId: activityId
      });
    })
  ));
  private readonly _simulatingActivityMap: { [activityId: string]: IActivity };
  stopWebsiteProductActivitySimulation$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityAction.IMPORT_HOSTED_TEMPLATE, ActivityAction.IMPORT_TEMPLATE
    ]).includes(action.payload.action)),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityStatus.DONE,
      ActivityStatus.FAILED
    ]).includes(action.payload.status)),
    filter((action: AddActivity): boolean => {
      const activity: IActivity = action.payload;
      return !!this._simulatingActivityMap[activity.id];
    }),
    map((action: AddActivity) => {
      const activity: IActivity = action.payload;
      delete this._simulatingActivityMap[activity.id];
      const activityTemplate: any = {websiteId: activity.websiteId};
      return new builderTemplatesActions.LoadImportingBuilderTemplateSuccess(activityTemplate);

    })
  ));

  constructor(
    private _store: Store<DbStoreState>,
    private _actions$: Actions,
    private _builderTemplatesService: BuilderTemplatesService
  ) {
    this._pendingActivityMap = {};
    this._simulatingActivityMap = {};
  }

  public simulateActivityProgress(baseActivity: IActivity): void {
    this._simulatingActivityMap[baseActivity.id] = baseActivity;

    const interval = setInterval((): void => {
      const previousActivity: IActivity = this._simulatingActivityMap[baseActivity.id];
      if (!previousActivity || previousActivity.progress >= 98) {
        clearInterval(interval);
        return;
      }

      let nextProgress: number;
      if (previousActivity.progress < 50) {
        nextProgress = previousActivity.progress + 3;
      } else if (previousActivity.progress < 80) {
        nextProgress = previousActivity.progress + 2;
      } else {
        nextProgress = previousActivity.progress + 1;
      }

      const activity: IActivity = <IActivity>{};
      activity.id = previousActivity.id;
      activity.websiteId = previousActivity.websiteId;
      activity.service = previousActivity.service;
      activity.type = previousActivity.type;
      activity.action = previousActivity.action;
      activity.date = new Date();
      activity.status = previousActivity.status;
      activity.progress = nextProgress;
      activity.message = previousActivity.message;
      activity.meta = JSON.parse(JSON.stringify(previousActivity.meta));

      this._simulatingActivityMap[activity.id] = activity;

      this._store.dispatch(new UpdateOngoingActivityState({
        websiteId: activity.websiteId,
        entity: activity
      }));
    }, 500);
  }

  private generateImportTemplatePendingActivity(activityId: string, websiteId: number): IActivity {
    const activity: IActivity = <IActivity>{};
    activity.id = activityId;
    activity.websiteId = websiteId;
    activity.service = '';
    activity.type = ActivityType.ACTIVITY;
    activity.action = ActivityAction.IMPORT_HOSTED_TEMPLATE || ActivityAction.IMPORT_TEMPLATE;
    activity.date = new Date();
    activity.status = ActivityStatus.PENDING;
    activity.progress = 0;
    activity.message = '';
    activity.meta = null;
    return activity;
  }
}
